import React, { useMemo } from 'react'

import { Skeleton } from 'antd'
import { Flex, Header, VStack } from 'govwell-ui'
import { AICADepartmentMetric } from 'types/graphql'

import { PieChart } from 'src/components/Visualizations/PieChart/PieChart'
import { TailwindColor } from 'src/constants/theme/tailwind'

import CategoryCard from './CategoryCard'

// Category colors for visual distinction
const MAX_CATEGORIES_TO_DISPLAY = 6
const CATEGORY_COLORS: string[] = [
  TailwindColor.Green600,
  TailwindColor.Blue500,
  TailwindColor.Purple600,
  TailwindColor.Cyan600,
  TailwindColor.Orange500,
  TailwindColor.Gray400,
]

const getCategoryColor = (index: number): string => {
  return CATEGORY_COLORS[index % CATEGORY_COLORS.length] ?? TailwindColor.Gray400
}

type Props = {
  topDepartments: AICADepartmentMetric[]
  isLoading: boolean
}

const CategoriesSection = ({ topDepartments, isLoading }: Props) => {
  // Prepare pie chart data
  const pieChartData = useMemo(() => {
    return topDepartments.slice(0, MAX_CATEGORIES_TO_DISPLAY).map((dept, index) => ({
      id: dept.topicName,
      label: dept.topicName,
      value: dept.questionCount,
      color: getCategoryColor(index),
    }))
  }, [topDepartments])

  // Get categories to display
  const categories = useMemo(() => {
    return topDepartments.slice(0, MAX_CATEGORIES_TO_DISPLAY).map((dept, index) => ({
      ...dept,
      color: getCategoryColor(index),
    }))
  }, [topDepartments])

  if (isLoading) {
    return (
      <VStack gap={4}>
        <Header title="Question Categories" />
        <Skeleton active />
      </VStack>
    )
  }

  if (topDepartments.length === 0) {
    return null
  }

  return (
    <VStack gap={4}>
      <Header
        title="Question Categories"
        subtitle="Distribution of questions across different topic areas"
      />
      <Flex direction="row" gap={4} wrap="wrap">
        <PieChart
          data={pieChartData}
          label="Question Distribution by Category"
          labelTooltip="The distribution of questions by topic category"
          colors={CATEGORY_COLORS}
        />
      </Flex>
      <Flex direction="row" gap={4} wrap="wrap">
        {categories.map((category) => (
          <CategoryCard
            key={category.topicName}
            topicName={category.topicName}
            questionCount={category.questionCount}
            percentageOfTotal={category.percentageOfTotal}
            borderColor={category.color}
          />
        ))}
      </Flex>
    </VStack>
  )
}

export default React.memo(CategoriesSection)
